import { useEffect, useState } from 'react'
import type { ShiftTemplate } from './api'
import { translate, type Lang } from './i18n'
import { browserZone, knownAgencyZone, zoneLabel } from './time'

/**
 * Los turnos de la agencia (§16.3; §6.3).
 *
 * Venían fijos en el producto: tres de ocho horas. Una corporación con turnos de doce, o con un
 * cuarto de refuerzo los fines de semana, podía capturar el código en sus datos pero el sistema no
 * sabía a qué hora empezaba, así que el reloj del turno y la liberación progresiva calculaban
 * sobre horarios que no eran los suyos.
 *
 * **Guardar reemplaza la lista completa**, igual que la disponibilidad del turno: con un guardado
 * parcial, quitar un turno sería indistinguible de no haberlo mandado.
 */
export function ShiftTemplatesEditor({
  lang, templates, busy, onSave,
}: {
  lang: Lang
  templates: ShiftTemplate[]
  busy: boolean
  /** Si se guardó: sin decirlo, la única forma de saberlo era recargar (H-74). */
  onSave: (templates: ShiftTemplate[]) => Promise<boolean>
}) {
  const [filas, setFilasEditadas] = useState<ShiftTemplate[]>(templates)
  const [guardado, setGuardado] = useState(false)
  // Cualquier cambio después de guardar deja de estar guardado.
  const setFilas = (nuevas: ShiftTemplate[]) => { setFilasEditadas(nuevas); setGuardado(false) }

  // Lo que llega del servidor manda mientras no se esté editando algo distinto.
  useEffect(() => setFilasEditadas(templates), [templates])

  const cambiar = (i: number, cambio: Partial<ShiftTemplate>) =>
    setFilas(filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)))

  const agregar = () => setFilas([...filas, { code: '', name: '', start: '06:00', end: '14:00', hours: 0, crossesMidnight: false }])

  // Sin ninguno no hay turnos, y sin turnos no hay nada que planear: el servidor lo rechaza y la
  // pantalla no debería dejar llegar hasta ahí.
  const quitar = (i: number) => filas.length > 1 && setFilas(filas.filter((_, j) => j !== i))

  const incompletas = filas.some((f) => !f.code.trim() || !f.name.trim() || f.start === f.end)
  const repetidos = new Set(filas.map((f) => f.code.trim().toUpperCase())).size !== filas.length

  /** Cuánto dura, calculado aquí solo para mostrarlo mientras se edita. */
  const duracion = (f: ShiftTemplate) => {
    const [h1 = NaN, m1 = NaN] = f.start.split(':').map(Number)
    const [h2 = NaN, m2 = NaN] = f.end.split(':').map(Number)
    if ([h1, m1, h2, m2].some(Number.isNaN)) return null
    const minutos = (h2 * 60 + m2 - (h1 * 60 + m1) + 1440) % 1440
    return minutos === 0 ? null : minutos / 60
  }

  return (
    <section className="panel">
      <h2>{translate(lang, 'shifts.title')}</h2>
      <p className="notice">{translate(lang, 'shifts.help', [zoneLabel(new Date(), knownAgencyZone() ?? browserZone())])}</p>

      <table className="compare">
        <thead>
          <tr>
            <th>{translate(lang, 'shifts.code')}</th>
            <th>{translate(lang, 'shifts.name')}</th>
            <th>{translate(lang, 'shifts.start')}</th>
            <th>{translate(lang, 'shifts.end')}</th>
            <th>{translate(lang, 'shifts.hours')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => {
            const horas = duracion(f)
            return (
              <tr key={i}>
                <td>
                  <input
                    value={f.code} disabled={busy} style={{ width: 90 }}
                    onChange={(e) => cambiar(i, { code: e.target.value.toUpperCase() })}
                    aria-label={`${translate(lang, 'shifts.code')} ${i + 1}`}
                  />
                </td>
                <td>
                  <input
                    value={f.name} disabled={busy} style={{ width: 120 }}
                    onChange={(e) => cambiar(i, { name: e.target.value })}
                    aria-label={`${translate(lang, 'shifts.name')} ${i + 1}`}
                  />
                </td>
                <td>
                  <input
                    type="time" value={f.start} disabled={busy}
                    onChange={(e) => cambiar(i, { start: e.target.value })}
                    aria-label={`${translate(lang, 'shifts.start')} ${i + 1}`}
                  />
                </td>
                <td>
                  <input
                    type="time" value={f.end} disabled={busy}
                    onChange={(e) => cambiar(i, { end: e.target.value })}
                    aria-label={`${translate(lang, 'shifts.end')} ${i + 1}`}
                  />
                </td>
                {/* La duración se muestra mientras se edita porque es donde se ve el error: un
                    turno de veintitrés horas casi siempre es un dedo en la tecla equivocada, y sin
                    esto no se nota hasta que el plan sale raro. */}
                <td className="num">
                  {horas === null ? '—' : translate(lang, 'shifts.duration', [horas.toFixed(1)])}
                  {horas !== null && f.end <= f.start && ` · ${translate(lang, 'shifts.overnight')}`}
                </td>
                <td>
                  <button disabled={busy || filas.length <= 1} onClick={() => quitar(i)}>
                    {translate(lang, 'shifts.remove')}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Solapamientos y huecos no se impiden: una agencia puede tener un turno de refuerzo que
          monta sobre otro, o no cubrir la madrugada del domingo. Se avisa y decide ella. */}
      {repetidos && <p className="notice warning">{translate(lang, 'shifts.duplicate')}</p>}

      <div className="row-line">
        <button disabled={busy} onClick={agregar}>{translate(lang, 'shifts.add')}</button>
        <div className="spacer" />
        {guardado && <span className="chip ok" role="status">{translate(lang, 'shifts.saved')}</span>}
        <button disabled={busy || incompletas || repetidos} onClick={() => void onSave(filas).then(setGuardado)}>
          {translate(lang, 'shifts.save')}
        </button>
      </div>
      {incompletas && <p className="notice">{translate(lang, 'shifts.incomplete')}</p>}
    </section>
  )
}
